import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';

const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  color: #333;
  margin: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
  
  &.primary {
    background-color: #007bff;
    color: white;
    
    &:hover {
      background-color: #0056b3;
    }
  }
  
  &.danger {
    background-color: #dc3545;
    color: white;
    
    &:hover {
      background-color: #c82333;
    }
  }
  
  &.secondary {
    background-color: #6c757d;
    color: white;
    
    &:hover {
      background-color: #545b62;
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SearchBar = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  align-items: center;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const Th = styled.th`
  background-color: #f8f9fa;
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #dee2e6;
`;

const Td = styled.td`
  padding: 12px;
  border-bottom: 1px solid #dee2e6;
  
  &:last-child {
    text-align: center;
  }
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  
  &.used {
    background-color: #d4edda;
    color: #155724;
  }
  
  &.unused {
    background-color: #fff3cd;
    color: #856404;
  }
  
  &.expired {
    background-color: #ffebee;
    color: #c62828;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #333;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  resize: vertical;
  min-height: 100px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
  gap: 15px;
`;

const PaginationInfo = styled.div`
  color: #666;
  font-size: 14px;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
`;

const PageButton = styled.button`
  padding: 6px 12px;
  border: 1px solid #ddd;
  background: white;
  cursor: pointer;
  border-radius: 4px;
  font-size: 14px;
  min-width: 36px;
  
  &.active {
    background-color: #007bff;
    color: white;
    border-color: #007bff;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled):not(.active) {
    background-color: #f8f9fa;
  }
`;

const PageInput = styled.input`
  width: 60px;
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  text-align: center;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 15px;
`;

const SuccessMessage = styled.div`
  color: #155724;
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 15px;
`;

function PlanetUserManagement() {
  const [planetUsers, setPlanetUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 20
  });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // all, used, unused, expired
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    planet_code: '',
    planet_nickname: '',
    expire_date: ''
  });
  const [editFormData, setEditFormData] = useState({
    expire_date: ''
  });
  const [batchData, setBatchData] = useState('');

  useEffect(() => {
    fetchPlanetUsers();
  }, [pagination.current_page, search, statusFilter]);

  const fetchPlanetUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/admin/planet-users', {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params: {
          page: pagination.current_page,
          limit: pagination.items_per_page,
          search: search,
          status: statusFilter
        }
      });

      if (response.data.success) {
        setPlanetUsers(response.data.data.planet_users);
        setPagination(response.data.data.pagination);
      } else {
        setError(response.data.message);
      }
    } catch (error) {
      console.error('获取星球用户列表失败:', error);
      setError('获取星球用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current_page: 1 }));
    fetchPlanetUsers();
  };

  // 分页处理函数
  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.total_pages) {
      setPagination(prev => ({ ...prev, current_page: page }));
    }
  };

  const handlePageInputChange = (e) => {
    const page = parseInt(e.target.value);
    if (!isNaN(page) && page >= 1 && page <= pagination.total_pages) {
      handlePageChange(page);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post('/api/admin/planet-users', formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setSuccess(response.data.message);
        setFormData({ planet_code: '', planet_nickname: '', expire_date: '' });
        setShowAddModal(false);
        fetchPlanetUsers();
      } else {
        setError(response.data.message);
      }
    } catch (error) {
      console.error('添加星球用户失败:', error);
      setError('添加星球用户失败');
    }
  };

  const handleBatchImport = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      // 解析批量数据
      const lines = batchData.trim().split('\n');
      const users = [];

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const parts = line.split(',').map(part => part.trim());
        if (parts.length !== 2) {
          setError(`第${i + 1}行格式错误，应为：星球编号,昵称`);
          return;
        }

        users.push({
          planet_code: parts[0],
          planet_nickname: parts[1]
        });
      }

      if (users.length === 0) {
        setError('请输入有效的用户数据');
        return;
      }

      const token = localStorage.getItem('adminToken');
      const response = await axios.post('http://localhost:8000/api/planet_users.php', {
        action: 'batch',
        users: users
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setSuccess(response.data.message);
        if (response.data.data.errors.length > 0) {
          setError('部分导入失败：\n' + response.data.data.errors.join('\n'));
        }
        setBatchData('');
        setShowBatchModal(false);
        fetchPlanetUsers();
      } else {
        setError(response.data.message);
      }
    } catch (error) {
      console.error('批量导入失败:', error);
      setError('批量导入失败');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditFormData({
      expire_date: user.expire_date || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.put(`/api/admin/planet-users/${editingUser.id}`, {
        expire_date: editFormData.expire_date
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setSuccess(response.data.message);
        setShowEditModal(false);
        setEditingUser(null);
        setEditFormData({ expire_date: '' });
        fetchPlanetUsers();
      } else {
        setError(response.data.message);
      }
    } catch (error) {
      console.error('更新星球用户失败:', error);
      setError('更新星球用户失败');
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('确定要删除这个星球用户吗？')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.delete(`/api/admin/planet-users/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setSuccess(response.data.message);
        fetchPlanetUsers();
      } else {
        setError(response.data.message);
      }
    } catch (error) {
      console.error('删除星球用户失败:', error);
      setError('删除星球用户失败');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    // 转换为北京时间 (UTC+8)
    const beijingTime = new Date(date.getTime() + (8 * 60 * 60 * 1000));
    return beijingTime.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Shanghai'
    });
  };

  // 渲染分页组件
  const renderPagination = () => {
    if (pagination.total_pages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, pagination.current_page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pagination.total_pages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <PaginationContainer>
        <PaginationInfo>
          显示第 {(pagination.current_page - 1) * pagination.items_per_page + 1} - {Math.min(pagination.current_page * pagination.items_per_page, pagination.total_items)} 条，共 {pagination.total_items} 条记录
        </PaginationInfo>
        <PaginationControls>
          <PageButton
            disabled={pagination.current_page === 1}
            onClick={() => handlePageChange(1)}
          >
            首页
          </PageButton>
          <PageButton
            disabled={pagination.current_page === 1}
            onClick={() => handlePageChange(pagination.current_page - 1)}
          >
            上一页
          </PageButton>
          
          {startPage > 1 && (
            <>
              <PageButton onClick={() => handlePageChange(1)}>1</PageButton>
              {startPage > 2 && <span>...</span>}
            </>
          )}
          
          {pages.map(page => (
            <PageButton
              key={page}
              className={page === pagination.current_page ? 'active' : ''}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </PageButton>
          ))}
          
          {endPage < pagination.total_pages && (
            <>
              {endPage < pagination.total_pages - 1 && <span>...</span>}
              <PageButton onClick={() => handlePageChange(pagination.total_pages)}>{pagination.total_pages}</PageButton>
            </>
          )}
          
          <PageButton
            disabled={pagination.current_page === pagination.total_pages}
            onClick={() => handlePageChange(pagination.current_page + 1)}
          >
            下一页
          </PageButton>
          <PageButton
            disabled={pagination.current_page === pagination.total_pages}
            onClick={() => handlePageChange(pagination.total_pages)}
          >
            末页
          </PageButton>
          
          <span style={{ margin: '0 10px', color: '#666' }}>跳转到</span>
          <PageInput
            type="number"
            min="1"
            max={pagination.total_pages}
            value={pagination.current_page}
            onChange={handlePageInputChange}
          />
          <span style={{ color: '#666' }}>页</span>
        </PaginationControls>
      </PaginationContainer>
    );
  };

  return (
    <Container>
      <Header>
        <Title>星球用户管理</Title>
        <ButtonGroup>
          <Button className="primary" onClick={() => setShowAddModal(true)}>
            添加用户
          </Button>
          <Button className="secondary" onClick={() => setShowBatchModal(true)}>
            批量导入
          </Button>
        </ButtonGroup>
      </Header>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      <SearchBar>
        <Input
          type="text"
          placeholder="搜索星球编号或昵称"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
        />
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">全部状态</option>
          <option value="unused">未使用</option>
          <option value="used">已使用</option>
          <option value="expired">已过期</option>
        </Select>
        <Button className="primary" onClick={handleSearch}>
          搜索
        </Button>
      </SearchBar>

      <Table>
        <thead>
          <tr>
            <Th>ID</Th>
            <Th>星球编号</Th>
            <Th>昵称</Th>
            <Th>状态</Th>
            <Th>到期时间</Th>
            <Th>使用者</Th>
            <Th>创建时间</Th>
            <Th>使用时间</Th>
            <Th>操作</Th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <Td colSpan="8" style={{ textAlign: 'center' }}>加载中...</Td>
            </tr>
          ) : planetUsers.length === 0 ? (
            <tr>
              <Td colSpan="8" style={{ textAlign: 'center' }}>暂无数据</Td>
            </tr>
          ) : (
            planetUsers.map((user) => (
              <tr key={user.id}>
                <Td>{user.id}</Td>
                <Td>{user.planet_code}</Td>
                <Td>{user.planet_nickname}</Td>
                <Td>
                  <StatusBadge className={
                    user.is_expired ? 'expired' : 
                    user.is_used ? 'used' : 'unused'
                  }>
                    {user.is_expired ? '已过期' : 
                     user.is_used ? '已使用' : '未使用'}
                  </StatusBadge>
                </Td>
                <Td>{user.expire_date || '-'}</Td>
                <Td>{user.used_by_username || '-'}</Td>
                <Td>{formatDate(user.created_at)}</Td>
                <Td>{formatDate(user.used_at)}</Td>
                <Td>
                  {user.is_expired && (
                    <Button
                      className="primary"
                      onClick={() => handleEditUser(user)}
                      style={{ marginRight: '8px' }}
                    >
                      编辑
                    </Button>
                  )}
                  {!user.is_used && (
                    <Button
                      className="danger"
                      onClick={() => handleDeleteUser(user.id)}
                    >
                      删除
                    </Button>
                  )}
                </Td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {renderPagination()}

      {/* 添加用户模态框 */}
      {showAddModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>添加星球用户</ModalTitle>
              <CloseButton onClick={() => setShowAddModal(false)}>×</CloseButton>
            </ModalHeader>
            <form onSubmit={handleAddUser}>
              <FormGroup>
                <Label>星球编号</Label>
                <Input
                  type="text"
                  value={formData.planet_code}
                  onChange={(e) => setFormData(prev => ({ ...prev, planet_code: e.target.value }))}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>昵称</Label>
                <Input
                  type="text"
                  value={formData.planet_nickname}
                  onChange={(e) => setFormData(prev => ({ ...prev, planet_nickname: e.target.value }))}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>到期时间 (可选)</Label>
                <Input
                  type="date"
                  value={formData.expire_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, expire_date: e.target.value }))}
                />
              </FormGroup>
              <ButtonGroup>
                <Button type="submit" className="primary">添加</Button>
                <Button type="button" className="secondary" onClick={() => setShowAddModal(false)}>取消</Button>
              </ButtonGroup>
            </form>
          </ModalContent>
        </Modal>
      )}

      {/* 批量导入模态框 */}
      {showBatchModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>批量导入星球用户</ModalTitle>
              <CloseButton onClick={() => setShowBatchModal(false)}>×</CloseButton>
            </ModalHeader>
            <form onSubmit={handleBatchImport}>
              <FormGroup>
                <Label>用户数据（每行一个用户，格式：星球编号,昵称）</Label>
                <TextArea
                  value={batchData}
                  onChange={(e) => setBatchData(e.target.value)}
                  placeholder="例如：\n1984,zlbigger\n1988,zlbigger1"
                  required
                />
              </FormGroup>
              <ButtonGroup>
                <Button type="submit" className="primary">导入</Button>
                <Button type="button" className="secondary" onClick={() => setShowBatchModal(false)}>取消</Button>
              </ButtonGroup>
            </form>
          </ModalContent>
        </Modal>
      )}

      {/* 编辑用户模态框 */}
      {showEditModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>编辑星球用户</ModalTitle>
              <CloseButton onClick={() => setShowEditModal(false)}>×</CloseButton>
            </ModalHeader>
            <form onSubmit={handleUpdateUser}>
              <FormGroup>
                <Label>星球编号</Label>
                <Input
                  type="text"
                  value={editingUser?.planet_code || ''}
                  disabled
                />
              </FormGroup>
              <FormGroup>
                <Label>昵称</Label>
                <Input
                  type="text"
                  value={editingUser?.planet_nickname || ''}
                  disabled
                />
              </FormGroup>
              <FormGroup>
                <Label>到期时间</Label>
                <Input
                  type="date"
                  value={editFormData.expire_date}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, expire_date: e.target.value }))}
                  required
                />
              </FormGroup>
              <ButtonGroup>
                <Button type="submit" className="primary">更新</Button>
                <Button type="button" className="secondary" onClick={() => setShowEditModal(false)}>取消</Button>
              </ButtonGroup>
            </form>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
}

export default PlanetUserManagement;